import React, { useState } from "react";
import { Gift, MailOpen, Sparkles } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";
import { PHOTOS } from "@/lib/data";

const Gifting = () => {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  return (
    <section id="gifting" className="relative py-16 sm:py-24 overflow-hidden bg-aurora">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Gift a Christmas Ticket"
              title={
                <>
                  Bring light to a grieving heart <em className="text-gold-gradient italic">this Christmas</em>
                </>
              }
              sub="There is no harder gift to shop for than grief. This Christmas, wrap something different under the tree — an evening in the new year where absence is honored, hope is restored, and the people they miss feel close again."
            />

            <div className="mt-10 space-y-6">
              {[
                { icon: Gift, t: "Choose their seats", d: "Pick the exact seats you're gifting on the live map — the VIP Experience if you really love them." },
                { icon: MailOpen, t: "Add your message", d: "At checkout, switch on 'This is a gift' and write a note only you could write. They'll receive a festive digital gift certificate." },
                { icon: Sparkles, t: "We deliver the light", d: "Their gift pass arrives with your message — their seats waiting under their name at the Saban door." },
              ].map((s, i) => (
                <Reveal key={s.t} delay={i * 0.1}>
                  <div className="flex gap-5 items-start">
                    <div className="shrink-0 w-11 h-11 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center">
                      <s.icon size={18} className="text-[#F3D068]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-[#FDFBF7] font-medium">{s.t}</h3>
                      <p className="text-sm text-[#9CA3AF] font-light mt-1 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3}>
              <button
                data-testid="gifting-start-cta"
                onClick={() => scrollToSection("seating")}
                className="btn-gold rounded-full px-9 py-4 text-sm font-bold tracking-wide mt-10"
              >
                Choose Seats to Gift
              </button>
            </Reveal>
          </div>

          {/* Interactive gift card preview */}
          <Reveal delay={0.15}>
            <div className="relative">
              <img
                src={PHOTOS.greeting}
                alt="Susan Grau embracing a guest at a live event"
                className="w-full aspect-[4/3] object-cover object-top rounded-t-[28px]"
              />
              <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0B1021] to-transparent rounded-t-[28px]" />
              <div className="glass rounded-[22px] p-7 -mt-16 relative mx-6 border-[#F3D068]/25">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3D068] mb-4">
                  A Holiday Gift For You · A View from the Top — Live
                </p>
                <p className="font-display italic text-xl text-[#FDFBF7] min-h-[64px] leading-snug">
                  {name ? `${name}, ` : "For someone you love, "}
                  {note || "this seat is saved for you — an evening with the other side, and a night off from being strong."}
                </p>
                <p className="text-xs text-[#8A99B5] mt-4">Thursday, January 14, 2027 · The Saban Theatre</p>
              </div>

              <div className="mt-6 space-y-3">
                <input
                  data-testid="gift-preview-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Try it — type their name…"
                  maxLength={40}
                  className="w-full bg-[#131B36]/80 border border-white/10 rounded-full px-6 py-3.5 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/70 focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
                />
                <textarea
                  data-testid="gift-preview-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="…and the words you'd want to hear yourself…"
                  rows={2}
                  maxLength={140}
                  className="w-full bg-[#131B36]/80 border border-white/10 rounded-3xl px-6 py-3.5 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/70 focus:outline-none focus:border-[#D4AF37]/60 transition-colors resize-none"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Gifting;
